import React from 'react';
import { CheckCircle2, X } from 'lucide-react';
import { usePwaInstall } from '../hooks/usePwaInstall';

function HelpModal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 px-4 py-6 flex items-center justify-center">
      <div className="w-full max-w-sm rounded-2xl border border-white/15 bg-[#1b1b1b] p-5 text-slate-100 shadow-2xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close install help"
            className="h-8 w-8 rounded-full border border-white/20 grid place-items-center hover:bg-white/10 transition"
          >
            <X size={14} />
          </button>
        </div>
        {children}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-[#8b0000] px-4 py-2 text-sm font-semibold text-white hover:bg-[#6f0000] transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PwaInstallOverlays() {
  const {
    inAppBrowser,
    installPhase,
    setShowInstallHelp,
    setShowIosInstallHelp,
    showInstallHelp,
    showIosInstallHelp,
    showToast,
    toastMessage,
  } = usePwaInstall();

  const showProgress = installPhase === 'installing' || installPhase === 'success';

  return (
    <>
      {showProgress && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div className="mx-auto max-w-md rounded-2xl border border-brand-gold/35 bg-[#1b1b1b]/95 px-4 py-3 shadow-2xl backdrop-blur-sm">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-white">
                {installPhase === 'success' ? 'Install complete' : 'Installing EchoJham Booking…'}
              </p>
              {installPhase === 'success' && (
                <CheckCircle2 size={18} className="shrink-0 text-brand-gold" />
              )}
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className={
                  installPhase === 'success'
                    ? 'h-full w-full rounded-full bg-brand-gold transition-all duration-500'
                    : 'pwa-install-progress-bar h-full rounded-full bg-brand-gold'
                }
              />
            </div>
            {installPhase === 'installing' && (
              <p className="mt-2 text-xs text-slate-400">
                This can take a moment on some phones. Please keep this page open.
              </p>
            )}
          </div>
        </div>
      )}

      {showToast && toastMessage && (
        <div className="fixed inset-x-0 top-0 z-[80] flex justify-center px-4 pt-[calc(0.85rem+env(safe-area-inset-top))] pointer-events-none">
          <div className="pwa-install-toast max-w-sm rounded-xl border border-brand-gold/40 bg-[#1b1b1b] px-4 py-3 text-sm font-medium text-white shadow-2xl">
            {toastMessage}
          </div>
        </div>
      )}

      {showIosInstallHelp && (
        <HelpModal title="Install Echo Jham" onClose={() => setShowIosInstallHelp(false)}>
          <p className="mt-3 text-sm text-slate-300">
            On iPhone/iPad, open your browser menu and tap <strong>Share</strong>, then choose{' '}
            <strong>Add to Home Screen</strong>. Phone notifications only work after the app is installed this way.
          </p>
        </HelpModal>
      )}

      {showInstallHelp && (
        <HelpModal title="Install Echo Jham" onClose={() => setShowInstallHelp(false)}>
          {inAppBrowser ? (
            <>
              <p className="mt-3 text-sm text-slate-300">
                Install is blocked inside this in-app browser (for example Instagram or Facebook).
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Open this page in <strong>Chrome</strong> or <strong>Safari</strong>, then tap Install App again.
              </p>
            </>
          ) : (
            <>
              <p className="mt-3 text-sm text-slate-300">
                Install prompt is not available yet in this browser session.
              </p>
              <p className="mt-2 text-sm text-slate-300">
                Open the browser menu and choose <strong>Install App</strong> or{' '}
                <strong>Add to Home screen</strong>.
              </p>
              <p className="mt-2 text-xs text-slate-400">
                Note: install works on HTTPS (or localhost) and requires the production build with service worker.
              </p>
            </>
          )}
        </HelpModal>
      )}
    </>
  );
}
